import type { CreateTrasladoPayload, TrasladoLinea } from '../../../types/traslados'
import {
  getFlexyAlmacenStock,
  getCombinedStockDisplayOptions,
  getProductDetailDisplayStock
} from '../../../utils/productStockDisplayUtils'

export interface TrasladoValidationResult {
  valid: boolean
  errors: string[]
}

export function validateTrasladoLinea(linea: TrasladoLinea, index: number): string[] {
  const errors: string[] = []
  const row = index + 1

  if (!linea.sku?.trim()) {
    errors.push(`Línea ${row}: el SKU es obligatorio`)
  }
  if (!linea.producto?.trim()) {
    errors.push(`Línea ${row}: el nombre del producto es obligatorio`)
  }
  if (!Number.isFinite(linea.cantidad) || linea.cantidad <= 0) {
    errors.push(`Línea ${row}: la cantidad debe ser mayor a 0`)
  }

  return errors
}

export function validateCreateTrasladoPayload(
  payload: CreateTrasladoPayload
): TrasladoValidationResult {
  const errors: string[] = []

  if (!payload.idalmacen_origen) {
    errors.push('Selecciona el almacén de origen')
  }
  if (!payload.idalmacen_destino) {
    errors.push('Selecciona el almacén de destino')
  }
  if (
    payload.idalmacen_origen &&
    payload.idalmacen_destino &&
    payload.idalmacen_origen === payload.idalmacen_destino
  ) {
    errors.push('El almacén de origen y destino no pueden ser el mismo')
  }
  if (!payload.glosa?.trim()) {
    errors.push('La glosa es obligatoria')
  }
  if (!payload.lineas?.length) {
    errors.push('Agrega al menos una línea de producto')
  } else {
    payload.lineas.forEach((linea, index) => {
      errors.push(...validateTrasladoLinea(linea, index))
    })
  }

  return { valid: errors.length === 0, errors }
}

/** Stock disponible para una línea de traslado (prioriza almacén origen). */
export function getTrasladoLineaStockDisponible(
  productDetail: Record<string, unknown> | undefined,
  idalmacenOrigen: number | ''
): number {
  if (!productDetail) return 0

  if (idalmacenOrigen) {
    const fromAlmacen = getFlexyAlmacenStock(productDetail, idalmacenOrigen)
    if (fromAlmacen != null) return fromAlmacen
  }

  return getProductDetailDisplayStock(productDetail, getCombinedStockDisplayOptions())
}

export function getTrasladoStockBadgeClass(stock: number, cantidad: number): string {
  if (stock === 0) return 'bg-red-100 text-red-800'
  if (stock > 0 && stock < cantidad) return 'bg-yellow-100 text-yellow-800'
  return 'bg-green-100 text-green-800'
}

export function isTrasladoErrorEstado(estado: string): boolean {
  const normalized = estado.trim().toLowerCase()
  return normalized === 'error' || normalized === 'error_flexy'
}

export function isTrasladoDangerEstado(estado: string): boolean {
  const normalized = estado.trim().toLowerCase()
  return isTrasladoErrorEstado(normalized) || normalized === 'vencido'
}

export function getTrasladoEstadoBadgeClass(estado: string): string {
  const normalized = estado.trim().toLowerCase()
  const base =
    'inline-flex min-w-[4.75rem] items-center justify-center text-center'

  if (isTrasladoDangerEstado(normalized)) {
    return `${base} bg-red-100 text-red-800 border-red-200`
  }

  switch (normalized) {
    case 'aprobado':
      return `${base} bg-green-100 text-green-800 border-green-200`
    case 'anulado':
      return `${base} bg-gray-100 text-gray-700 border-gray-200`
    case 'pendiente':
      return `${base} bg-yellow-100 text-yellow-800 border-yellow-200`
    default:
      return `${base} bg-yellow-100 text-yellow-800 border-yellow-200`
  }
}

export function formatTrasladoEstado(estado: string): string {
  const labels: Record<string, string> = {
    pendiente: 'Pendiente',
    aprobado: 'Aprobado',
    anulado: 'Anulado',
    vencido: 'Vencido',
    error: 'Error',
    error_flexy: 'Error Flexy'
  }
  return labels[estado.trim().toLowerCase()] ?? estado
}
